import React, { useState } from "react";
import { Input, Divider, Button, Row, Space, Typography, Tooltip, message, Checkbox } from "antd";
const { Text } = Typography;

import { Droppable } from "react-beautiful-dnd";

import { PlusOutlined, EditOutlined, DeleteOutlined, StopOutlined } from "@ant-design/icons";

import PropTypes from "prop-types";
import { ClassificationNode } from "./ClassificationNode";
import { useCalculateComponentHeightSizeHook } from "../../hooks";
import { getTextStyle } from "./utils";
import styled from "styled-components";

const CardContainer = styled.div`
	position: relative;
	width: 375px;
	flex: 0 0 auto;
	margin-right: 8px;
	margin-bottom: 8px;
	z-index: ${props => props.isDragging ? 1000 : 1};
	box-shadow: ${props => props.isDragging ? "0 5px 10px rgba(0,0,0,0.15)" : "none"};
`;

const InputForCategoryCard = ({ id, onHandleAddSubCategory = () => {} }) => {
	const [categoryName, setCategoryName] = useState("");

	return (
		<Row>
			<Space direction="vertical" style={{ marginTop: 15, marginBottom: 15, width: "100%" }}>
				<Space.Compact style={{ width: "100%" }}>
					<Input
						maxLength={20}
						showCount
						placeholder="Nueva Sub-Categoría"
						value={categoryName}
						onChange={(e) => {								
							const filteredValue = e.target.value
								.replace(/-/g, "_");
								
							setCategoryName(filteredValue);
						}}
						autoFocus={true}
					/>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => {
							if (categoryName.trim() === "") {
								message.error("Por favor, ingresa un valor válido.");
								return;
							}

							setCategoryName("");
							onHandleAddSubCategory(id, categoryName);
						}}
					/>
				</Space.Compact>
			</Space>
		</Row>
	);
};

export const CategoryCard = ({
	cat, 
	index, 
	provided,
	snapshot,
	selectedItems = {},
	isSelected = false,
	showArchived = false,
	onHandleAddSubCategory = () => {},
	onHandleAddClassification = () => {},
	onRenameNode = () => {},
	onHandleDelete = () => {},
	onToggleSelection = () => {},
	onSelectWithChildren = () => {},
}) => {
	const { tableHeight } = useCalculateComponentHeightSizeHook({ heightDifference: 400 });
	
	const [isEditing, setIsEditing] = useState(false);		
	const [editedValue, setEditedValue] = useState("");
	
	const startEditing = (currentValue) => {		
		setIsEditing(true);
		setEditedValue(currentValue);
	};
	
	const saveEditing = () => {
		if (editedValue.trim() === "") {
			message.error("Por favor, ingresa un valor válido.");
			return;
		}
		
		onRenameNode(cat.id, editedValue);
		setIsEditing(false);
		setEditedValue("");
	};
		
	const visibleChildren = cat.children?.filter(child => showArchived || !child.archived) || [];
	
	return (
		<CardContainer
			ref={provided.innerRef}
			{...provided.draggableProps}
			{...provided.dragHandleProps}
			isDragging={snapshot.isDragging}
			status={cat.status}
			showArchived={showArchived}
		>
			<div												
				{...provided.dragHandleProps}
				style={{
					cursor: "grab",
					fontWeight: "bold",
					marginBottom: 4,
					textAlign: "left",													
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap"
				}}
			>
				<div style={{ display: "flex", alignItems: "center" }}>
					<Checkbox
						checked={isSelected}
						onChange={() => onToggleSelection(cat.id)}
						style={{ marginRight: 8 }}
					/>

					{isEditing ? (
						<Input
							value={editedValue}
							showCount
							onChange={(e) => {
								const filteredValue = e.target.value
									.replace(/-/g, "_");
									
								setEditedValue(filteredValue);
							}}                
							onPressEnter={saveEditing}
							onBlur={saveEditing}
							maxLength={20}
							style={{ width: 120 }}
							autoFocus
						/>
					) : (
						<Typography.Text
							style={{
								...getTextStyle(cat.status, cat.archived),
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "160px",
								display: "inline-block",
							}}
						>
							{index + 1}. {cat.classification_name}
						</Typography.Text>

					)}
				</div>

				<Space>
					<Tooltip title="Editar">
						<Button
							icon={<EditOutlined />}
							type="primary"
							ghost
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								if (isEditing) {
									saveEditing();
								} else {
									startEditing(cat.classification_name);
								}
							}}
						/>
					</Tooltip>					
					<Tooltip title={cat.isInUse ? "No se puede eliminar porque esta en uso" : "Eliminar"}>
						<Button
							icon={cat.isInUse ? <StopOutlined /> : <DeleteOutlined />}
							size="small"
							danger															
							onClick={() => onHandleDelete(cat.id)}
							disabled={cat.isInUse || !cat.archived}
						/>
					</Tooltip>
				</Space>
			</div>
			
			<Divider style={{ margin: "10px 0" }} />
			
			<InputForCategoryCard id={cat.id} onHandleAddSubCategory={onHandleAddSubCategory}/>
														
			{visibleChildren.length > 0 ? (
				<Droppable droppableId={cat.id} type={`DEPTH-${cat.depth + 1}`}>
					{(provided) => (
						<div 
							ref={provided.innerRef}
							{...provided.droppableProps}
							style={{
								maxHeight: `${tableHeight}px`,
								overflowY: "auto",
								paddingRight: 8,
							}}
						>
							{cat.children.map((child, i) => (
								<ClassificationNode
									key={child.id}
									item={child}
									index={i}
									parentId={cat.id}
									depth={1}
									onAddClassificationToNode={onHandleAddClassification}
									onRenameNode={onRenameNode}
									onDeleteNode={onHandleDelete}									
									isSelected={selectedItems[child.id] || false}
									selectedItems={selectedItems}
									onToggleSelection={onToggleSelection}
									onSelectWithChildren={onSelectWithChildren}
									showArchived={showArchived}
								/>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			) : (
				<Text>
				No hay subcategorías ni clasificaciones,
					<br />
				empieza creando una subcategoría :)
				</Text>
			)}											
		</CardContainer>
	);
};

CategoryCard.propTypes = {
	cat: PropTypes.object.isRequired,
	index: PropTypes.string.isRequired,
	provided: PropTypes.object.isRequired,
	snapshot: PropTypes.DraggableStateSnapshot,
	onHandleAddSubCategory: PropTypes.func,
	onHandleAddClassification: PropTypes.func,
	onRenameNode: PropTypes.func,
	onHandleDelete: PropTypes.func,
	selectedItems: PropTypes.object,
	isSelected: PropTypes.bool,
	onToggleSelection: PropTypes.func,
	onSelectWithChildren: PropTypes.func,
	showArchived: PropTypes.bool,
};

InputForCategoryCard.displayName = "InputForCategoryCard";

InputForCategoryCard.propTypes = {
	id: PropTypes.string.isRequired,
	onHandleAddSubCategory: PropTypes.func,
};
